import type { BigDecimal } from "@solomon-labs/bigdecimal";
import {
    ArrowRightLongIcon,
    TooltipIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    Card,
    Skeleton,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import LegacySUSDvIcon from "../../assets/icons/LegacySUSDv.png";
import LegacyUSDvIcon from "../../assets/icons/LegacyUSDv.png";
import UsdcIcon from "../../assets/icons/USDC.svg";
import USDvIcon from "../../assets/logos/usdv-logomark.png";
import { cn } from "../../utils/cn";

import {
    formatUsdvMigrationAmount,
    formatUsdvMigrationDateTime,
    getUsdvMigrationCardCopy,
    getUsdvMigrationStakedAmount,
    getUsdvMigrationStepCount,
    hasUsdvMigrationBalance
} from "./model";
import type { UsdvMigrationCardConfig } from "./types";

const USDV_MIGRATION_EXIT_MS = 200;
/** How long a failure names itself before the button turns back into the
 *  retry action. The button stays clickable throughout. */
const USDV_MIGRATION_ERROR_REASON_MS = 3000;

export interface UsdvMigrationCardPresenceProps {
    config?: UsdvMigrationCardConfig;
    className?: string;
}

/** Keeps the last card state mounted while the full card collapses on exit. */
export function UsdvMigrationCardPresence({
    config,
    className
}: UsdvMigrationCardPresenceProps): React.ReactElement | null {
    const [renderedConfig, setRenderedConfig] =
        useState<UsdvMigrationCardConfig | null>(config ?? null);
    const [presenceState, setPresenceState] = useState<
        "visible" | "prepared" | "exiting"
    >("visible");
    const [exitHeight, setExitHeight] = useState<number | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (config) {
            setRenderedConfig(config);
            setExitHeight(null);
            setPresenceState("visible");
            return;
        }
        if (!renderedConfig) return;

        setExitHeight(contentRef.current?.offsetHeight ?? 0);
        setPresenceState("prepared");
        const frame = requestAnimationFrame(() => setPresenceState("exiting"));
        return (): void => cancelAnimationFrame(frame);
    }, [config, renderedConfig]);

    useEffect(() => {
        if (presenceState !== "exiting") return;

        const timer = setTimeout(() => {
            if (!config) setRenderedConfig(null);
        }, USDV_MIGRATION_EXIT_MS + 40);
        return (): void => clearTimeout(timer);
    }, [config, presenceState]);

    if (!renderedConfig) return null;

    return (
        <div
            data-usdv-migration-presence=""
            data-presence-state={presenceState}
            className={cn(
                "overflow-hidden transition-[height] duration-200 ease-[var(--ease-in-out-quart)] motion-reduce:transition-none",
                className
            )}
            style={
                exitHeight === null
                    ? undefined
                    : {
                          height: presenceState === "exiting" ? 0 : exitHeight
                      }
            }
            onTransitionEnd={(event) => {
                if (
                    event.target === event.currentTarget &&
                    presenceState === "exiting" &&
                    !config
                ) {
                    setRenderedConfig(null);
                }
            }}
        >
            <div
                ref={contentRef}
                className={cn(
                    "transition-opacity duration-150 ease-[var(--ease-out-quint)] motion-reduce:transition-none",
                    presenceState === "exiting" ? "opacity-0" : "opacity-100"
                )}
            >
                <UsdvMigrationCard {...renderedConfig} />
            </div>
        </div>
    );
}

export function UsdvMigrationCard({
    plan,
    state,
    onAction,
    confirmationKey,
    onDismiss,
    unavailable = false,
    onMigrate,
    queueLoadRecovery
}: UsdvMigrationCardConfig): React.ReactElement | null {
    const [confirmingKey, setConfirmingKey] = useState<string | null>(null);
    const [showsErrorReason, setShowsErrorReason] = useState(false);
    const currentConfirmationKey = confirmationKey ?? "default";
    const isConfirming = confirmingKey === currentConfirmationKey;
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const primaryActionButtonRef = useRef<HTMLButtonElement>(null);
    const shouldRestoreActionFocusRef = useRef(false);
    const hasQueueLoadRecovery = queueLoadRecovery !== undefined;
    const copy = getUsdvMigrationCardCopy(state, plan);
    const isCooldown = state.phase === "cooldown";
    const isClaimable = state.phase === "claimable";
    const isError = state.phase === "error";
    const isRunning = state.phase === "running";
    const isSuccess = state.phase === "success";
    const isConversionSuccess = isSuccess && state.kind === "conversion";
    const errorReasonKey = isError ? `${copy.title}:${state.retry}` : null;
    useEffect((): (() => void) | undefined => {
        if (errorReasonKey === null) {
            setShowsErrorReason(false);
            return undefined;
        }
        setShowsErrorReason(true);
        const timer = window.setTimeout(
            (): void => setShowsErrorReason(false),
            USDV_MIGRATION_ERROR_REASON_MS
        );
        return () => window.clearTimeout(timer);
    }, [errorReasonKey]);
    const hasPendingPostClaimUpgrade =
        isError &&
        state.claimSucceeded &&
        state.pendingClaimedLegacyUsdv !== null;
    const displayedLegacyUsdv = hasPendingPostClaimUpgrade
        ? (state.pendingClaimedLegacyUsdv ?? plan.legacyUsdv)
        : plan.legacyUsdv;
    const usesExplicitWithdrawalBuckets =
        plan.claimableUsdv !== undefined ||
        plan.coolingUsdv !== undefined ||
        plan.coolingEndsAt !== undefined;
    const isLegacyClaimableBucket =
        !usesExplicitWithdrawalBuckets &&
        (isClaimable ||
            (isRunning && state.action === "claim") ||
            (isError && state.retry === "claim"));
    const claimableUsdv =
        plan.claimableUsdv ??
        (isLegacyClaimableBucket ? getUsdvMigrationStakedAmount(plan) : null);
    const coolingUsdv =
        plan.coolingUsdv ??
        (isCooldown ? getUsdvMigrationStakedAmount(plan) : null);
    const coolingEndsAt =
        plan.coolingEndsAt ?? (isCooldown ? state.cooldownEndsAt : null);
    const isAllBucketPostClaimRecovery =
        hasPendingPostClaimUpgrade &&
        plan.stakedUsdv.gt(0) &&
        claimableUsdv?.gt(0) &&
        coolingUsdv?.gt(0);
    const showClaimableWithdrawal =
        claimableUsdv?.gt(0) && !isAllBucketPostClaimRecovery;
    /**
     * The card carries at most one Legacy USDv row and one Legacy sUSDv row.
     * Every bucket denominated in legacy USDv — the wallet balance, a
     * claimable withdrawal, a cooling withdrawal, and the cooldown left behind
     * by a partial recovery — is the same token at a different point in the
     * pipeline, so they add up into a single figure.
     */
    const postClaimCoolingUsdv =
        state.phase === "error" &&
        !plan.coolingUsdv?.gt(0) &&
        state.claimSucceeded &&
        state.cooldownEndsAt
            ? state.cooldownUsdv
            : null;
    const legacyRowAmount = [
        showClaimableWithdrawal ? claimableUsdv : null,
        coolingUsdv,
        postClaimCoolingUsdv
    ].reduce<BigDecimal>(
        (total, bucket) => (bucket ? total.add(bucket) : total),
        displayedLegacyUsdv
    );
    /** Anything still to migrate: legacy USDv in any bucket, or an active
     *  stake. While this is true the migration is not finished, whatever the
     *  last action returned. */
    const hasOutstandingMigration =
        legacyRowAmount.gt(0) ||
        plan.stakedUsdv.gt(0) ||
        !!plan.usdcUsdv?.gt(0);
    const showActiveStakedRow =
        plan.stakedUsdv.gt(0) &&
        !isCooldown &&
        (usesExplicitWithdrawalBuckets ||
            (!isLegacyClaimableBucket && !isCooldown));
    const migrationStepCount = getUsdvMigrationStepCount(plan);
    const queueIsLoading =
        queueLoadRecovery?.status === "loading" ||
        queueLoadRecovery?.status === "retrying";
    const motionStateDetail = isRunning
        ? `${state.action}:${state.legacy}`
        : isSuccess
          ? `${state.kind}:${state.claimableAfterStart}`
          : isError
            ? `${state.retry}:${state.claimSucceeded}`
            : isCooldown
              ? String(state.cooldownEndsAt.getTime())
              : state.phase;
    const motionStateKey = [
        state.phase,
        motionStateDetail,
        isConfirming ? "confirming" : "settled",
        queueLoadRecovery?.status ?? "balances-ready"
    ].join(":");

    useEffect(() => {
        if (
            state.phase !== "available" &&
            state.phase !== "claimable" &&
            state.phase !== "error"
        ) {
            setConfirmingKey(null);
        }
    }, [state.phase]);

    useEffect(() => {
        if (isConfirming) confirmButtonRef.current?.focus();
    }, [isConfirming]);

    useEffect(() => {
        if (!isConfirming && shouldRestoreActionFocusRef.current) {
            primaryActionButtonRef.current?.focus();
            shouldRestoreActionFocusRef.current = false;
        }
    }, [isConfirming]);

    if (
        !hasUsdvMigrationBalance(plan) &&
        state.phase !== "running" &&
        state.phase !== "cooldown" &&
        state.phase !== "claimable" &&
        state.phase !== "error" &&
        state.phase !== "success" &&
        !hasQueueLoadRecovery
    )
        return null;

    return (
        <Card
            asElement="section"
            surface="sunken"
            rounded={false}
            className="w-full p-4"
            aria-label="Legacy USDv Migration"
            role={
                isError || queueLoadRecovery?.status === "error"
                    ? "alert"
                    : "status"
            }
            aria-live={
                isError || queueLoadRecovery?.status === "error"
                    ? "assertive"
                    : "polite"
            }
        >
            <UsdvMigrationMotion
                transitionKey={motionStateKey}
                phase={state.phase}
                confirming={isConfirming}
            >
                <Text variant="body" className="text-foreground-neutral pb-3">
                    Legacy USDv Migration
                </Text>

                {hasQueueLoadRecovery &&
                    queueLoadRecovery?.status === "error" && (
                        <Text
                            variant="fine-print"
                            className="text-foreground-neutral pb-3"
                        >
                            Balance check failed. Try again.
                        </Text>
                    )}
                {hasQueueLoadRecovery && queueIsLoading && (
                    <Text
                        variant="fine-print"
                        className="text-foreground-neutral pb-3"
                    >
                        Checking balances...
                    </Text>
                )}

                <div className="flex flex-col">
                    {legacyRowAmount.gt(0) && (
                        <MigrationRow
                            label={
                                isConversionSuccess
                                    ? "Claimed USDv"
                                    : "Legacy USDv"
                            }
                            icon={
                                isConversionSuccess ? USDvIcon : LegacyUSDvIcon
                            }
                            amount={formatUsdvMigrationAmount(legacyRowAmount)}
                            loading={queueIsLoading}
                        />
                    )}
                    {isConversionSuccess && plan.legacyUsdv.eq(0) && (
                        <MigrationRow
                            label="Claimed USDv"
                            icon={USDvIcon}
                            amount={formatUsdvMigrationAmount(
                                plan.expectedStakedUsdv ?? plan.stakedUsdv
                            )}
                        />
                    )}
                    {plan.usdcUsdv?.gt(0) && (
                        <MigrationRow
                            label="USDC"
                            icon={UsdcIcon}
                            amount={formatUsdvMigrationAmount(plan.usdcUsdv)}
                        />
                    )}
                    {showActiveStakedRow && (
                        <MigrationRow
                            label="Legacy sUSDv"
                            icon={LegacySUSDvIcon}
                            amount={formatUsdvMigrationAmount(
                                getUsdvMigrationStakedAmount(plan)
                            )}
                            loading={queueIsLoading}
                        />
                    )}
                </div>

                {hasQueueLoadRecovery &&
                    queueLoadRecovery?.status !== "loading" && (
                        <Button
                            variant="sunken"
                            size="sm"
                            interaction="lifted"
                            outline
                            fullWidth
                            className="mt-1"
                            disabled={queueLoadRecovery.status === "retrying"}
                            onClick={queueLoadRecovery.onRetry}
                        >
                            {queueLoadRecovery.status === "retrying"
                                ? "Checking balances..."
                                : "Retry balance check"}
                        </Button>
                    )}

                {/* mb-1: the light-bar rail is drawn 0.25rem BELOW the button
                    box, and UsdvMigrationMotion clips with overflow-hidden at
                    exactly the content edge — without it the rail is cut to
                    ~1px. */}
                {copy.action && !hasQueueLoadRecovery && !isConfirming && (
                    <Button
                        ref={primaryActionButtonRef}
                        variant="inverse-raised"
                        size="md"
                        interaction="light-bar"
                        status={isError ? "error" : undefined}
                        fullWidth
                        className="mt-1 mb-1"
                        disabled={unavailable}
                        trailing={<ArrowRightLongIcon className="size-5" />}
                        onClick={
                            onMigrate ??
                            ((): void =>
                                setConfirmingKey(currentConfirmationKey))
                        }
                    >
                        {unavailable
                            ? "Coming soon"
                            : isError
                              ? showsErrorReason
                                  ? `Error: ${copy.title}`
                                  : `Retry Migrate (0/${migrationStepCount})`
                              : `Migrate (0/${migrationStepCount})`}
                    </Button>
                )}

                {copy.action && !hasQueueLoadRecovery && isConfirming && (
                    <div
                        className="mt-1 grid grid-cols-2 gap-2"
                        role="group"
                        aria-label="Confirm action"
                    >
                        <Button
                            variant="sunken"
                            size="sm"
                            interaction="lifted"
                            outline
                            fullWidth
                            onClick={() => {
                                shouldRestoreActionFocusRef.current = true;
                                setConfirmingKey(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            ref={confirmButtonRef}
                            variant="raised"
                            size="sm"
                            interaction="lifted"
                            fullWidth
                            onClick={() => {
                                setConfirmingKey(null);
                                void onAction();
                            }}
                        >
                            Confirm
                        </Button>
                    </div>
                )}

                {!copy.action &&
                    !hasQueueLoadRecovery &&
                    coolingUsdv?.gt(0) &&
                    coolingEndsAt && (
                        <TooltipProvider>
                            <Tooltip>
                                {/* The trigger wraps the button rather than
                                    the icon: status="pending" sets the native
                                    disabled attribute, and the base class
                                    carries disabled:pointer-events-none, so
                                    the button itself never sees the hover —
                                    it passes through to this span. */}
                                <TooltipTrigger
                                    render={
                                        <span className="mt-1 mb-1 block w-full cursor-help pb-1" />
                                    }
                                >
                                    <Button
                                        variant="inverse-raised"
                                        size="md"
                                        interaction="light-bar"
                                        status="pending"
                                        fullWidth
                                        trailing={
                                            <TooltipIcon className="size-5" />
                                        }
                                    >
                                        Pending Cooldown
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    Claim on{" "}
                                    {formatUsdvMigrationDateTime(coolingEndsAt)}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}

                {isSuccess && onDismiss && !hasOutstandingMigration && (
                    <Button
                        variant="raised"
                        size="sm"
                        interaction="lifted"
                        fullWidth
                        className="mt-1"
                        onClick={onDismiss}
                    >
                        Done
                    </Button>
                )}
            </UsdvMigrationMotion>
        </Card>
    );
}

interface UsdvMigrationMotionProps {
    children: React.ReactNode;
    transitionKey: string;
    phase: UsdvMigrationCardConfig["state"]["phase"];
    confirming: boolean;
}

function UsdvMigrationMotion({
    children,
    transitionKey,
    phase,
    confirming
}: UsdvMigrationMotionProps): React.ReactElement {
    const contentRef = useRef<HTMLDivElement>(null);
    const previousTransitionKeyRef = useRef(transitionKey);
    const [height, setHeight] = useState<number | null>(null);
    const [isEntering, setIsEntering] = useState(false);

    useLayoutEffect(() => {
        const content = contentRef.current;
        if (!content) return;

        const measure = (): void => {
            const nextHeight = content.offsetHeight;
            if (nextHeight > 0) {
                setHeight((currentHeight) =>
                    currentHeight === nextHeight ? currentHeight : nextHeight
                );
            }
        };

        measure();
        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(measure);
        observer.observe(content);
        return (): void => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (previousTransitionKeyRef.current === transitionKey) return;

        previousTransitionKeyRef.current = transitionKey;
        setIsEntering(true);
        const frame = requestAnimationFrame(() => setIsEntering(false));
        return (): void => cancelAnimationFrame(frame);
    }, [transitionKey]);

    return (
        <div
            data-usdv-migration-motion=""
            data-motion-phase={phase}
            data-motion-confirming={confirming}
            className="overflow-hidden transition-[height] duration-200 ease-[var(--ease-in-out-quart)] motion-reduce:transition-none"
            style={height === null ? undefined : { height }}
        >
            <div
                ref={contentRef}
                className={`transition-opacity duration-200 ease-[var(--ease-out-quint)] motion-reduce:transition-none ${
                    isEntering ? "opacity-60" : "opacity-100"
                }`}
            >
                {children}
            </div>
        </div>
    );
}

interface MigrationRowProps {
    label: string;
    amount: string;
    /** Token mark for rows that name a specific token. Decorative — the
     *  label already carries the name, so it stays out of the a11y tree. */
    icon?: string;
    /** Only for rows carrying information that lives nowhere else, such as a
     *  withdrawal's claim date. Token balance rows read as one line. */
    status?: string;
    /** Balances still in flight — show a placeholder rather than a figure the
     *  card cannot vouch for yet. */
    loading?: boolean;
}

function MigrationRow({
    label,
    amount,
    icon,
    status,
    loading
}: MigrationRowProps): React.ReactElement {
    return (
        <div className="flex items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-2">
                {icon && <img src={icon} alt="" className="size-6" />}
                <div>
                    <Text variant="body" className="text-foreground-muted">
                        {label}
                    </Text>
                    {status && (
                        <Text
                            variant="fine-print"
                            className="text-foreground-neutral"
                        >
                            {status}
                        </Text>
                    )}
                </div>
            </div>
            {loading ? (
                <Skeleton className="h-3.5 w-14" />
            ) : (
                <Text
                    variant="number"
                    className="text-foreground-base tabular-nums"
                >
                    {amount}
                </Text>
            )}
        </div>
    );
}
